import React, {
    useState, useEffect, useRef, useCallback
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text } from 'components/ui';
import {
    colors, space, shadows, zIndices, site, mediaQueries, fontWeights, fontSizes, lineHeights
} from 'style/config';
import { DebouncedScroll } from 'constants/events';
import localeUtils from 'utils/LanguageLocale';
import StickyLoyaltyProfileBannerEN from 'components/ProfileBanner/StickyLoyaltyProfileBanner/locale/StickyLoyaltyProfileBanner_en_US';
import StickyLoyaltyProfileBannerFR from 'components/ProfileBanner/StickyLoyaltyProfileBanner/locale/StickyLoyaltyProfileBanner_fr_CA';

const StickyLoyaltyProfileBanner = ({ netBeautyBankPointsAvailable = 0, availableBiPoints = 0 }) => {
    const [showBanner, setShowBanner] = useState(false);

    const scrollRef = useRef(null);
    const initialTopRef = useRef(0);

    const handleScroll = useCallback(() => {
        if (!scrollRef.current) {
            return;
        }

        const currentScroll = window.scrollY;

        // User has scrolled past the original element position
        const hasPassedElement = currentScroll > initialTopRef.current;

        setShowBanner(hasPassedElement);
    }, []);

    useEffect(() => {
        if (scrollRef.current) {
            initialTopRef.current = scrollRef.current.getBoundingClientRect().top + window.scrollY;
        }
    }, []);

    useEffect(() => {
        window.addEventListener(DebouncedScroll, handleScroll);

        handleScroll();

        return () => {
            window.removeEventListener(DebouncedScroll, handleScroll);
        };
    }, [handleScroll]);

    const pointsApplied = availableBiPoints - netBeautyBankPointsAvailable;
    const hasAppliedPoints = pointsApplied > 0;
    const pointsText = getText('pointsAvailable')(availableBiPoints?.toLocaleString());
    const appliedText = hasAppliedPoints
        ? getText('appliedMessage')(pointsApplied.toLocaleString(), netBeautyBankPointsAvailable?.toLocaleString())
        : null;

    return (
        <>
            {showBanner && (
                <Box css={styles.stickyContainer}>
                    <Text
                        is='span'
                        css={styles.points}
                    >
                        {pointsText}
                    </Text>
                    {hasAppliedPoints && (
                        <Text
                            is='span'
                            css={styles.messageText}
                        >
                            {appliedText}
                        </Text>
                    )}
                </Box>
            )}
            <div ref={scrollRef} />
        </>
    );
};

const getText = localeUtils.getLocalisedDataFromResource(StickyLoyaltyProfileBannerEN, StickyLoyaltyProfileBannerFR);
const styles = {
    stickyContainer: {
        position: 'sticky',
        top: site.headerHeight,
        width: '100vw',
        marginLeft: `-${space.container}px`,
        marginRight: `-${space.container}px`,
        transition: 'opacity 0.3s ease-in-out',
        zIndex: zIndices.fixedBar,
        backgroundColor: colors.white,
        boxShadow: shadows.light,
        padding: `${space[3]}px ${space[4]}px`,
        display: 'flex',
        justifyContent: 'space-between',
        lineHeight: lineHeights.none,
        [mediaQueries.sm]: {
            display: 'none'
        },
        [mediaQueries.smMax]: {
            fontSize: `${fontSizes.sm}px`
        }
    },
    points: {
        fontWeight: fontWeights.bold,
        flexShrink: 0
    },
    messageText: {
        textAlign: 'right'
    }
};

export default wrapFunctionalComponent(StickyLoyaltyProfileBanner, 'StickyLoyaltyProfileBanner');
